import axios from 'axios'
import { triggerLogoutHandler, triggerLicenseBlockedHandler } from '@/services/auth-events'
import { toast } from 'sonner'
import {
  getAccessToken,
  getSelectedTenantId,
  clearAuthStorage,
} from '@/services/storage'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorToast?: boolean
  }
}

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(process.env.NEXT_PUBLIC_CLIENT_ID && {
      'X-Client-ID': process.env.NEXT_PUBLIC_CLIENT_ID,
    }),
  },
})

let authExpired = false
let licenseBlocked = false

export class AuthExpiredError extends Error {
  constructor(message = 'Sessão expirada') {
    super(message)
    this.name = 'AuthExpiredError'
  }
}

/** Chamado quando o usuário regulariza o pagamento e tenta voltar ao painel. */
export function resetLicenseBlockedFlag(): void {
  licenseBlocked = false
}

api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const tenantId = getSelectedTenantId()
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId
    }
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.config?.skipGlobalErrorToast) {
      return Promise.reject(error)
    }

    if (error?.response) {
      const status = error.response.status
      const data = error.response.data

      if (status === 401) {
        if (!authExpired) {
          authExpired = true
          clearAuthStorage()
          triggerLogoutHandler()
        }

        if (error instanceof Error) {
          ; (error as Error & { isAuthExpired?: boolean }).isAuthExpired = true
        }
      }

      if (status === 403 && data?.error === 'license_blocked') {
        // Regularização de assinatura: redireciona uma única vez, sem empilhar toasts a cada requisição bloqueada.
        if (!licenseBlocked) {
          licenseBlocked = true
          triggerLicenseBlockedHandler(data?.message ?? null)
        }
      } else if (status === 403) {
        toast.error(data?.message || 'Acesso negado')
      }

      if (status === 409) {
        toast.error(data?.message || 'Conflito na requisição')
      }

      if (status === 422) {
        toast.error(data?.message || 'Dados inválidos')
      }

      if (status === 400) {
        toast.error(data?.message || 'Requisição inválida')
      }

      if (status === 404) {
        toast.error(data?.message || 'Recurso não encontrado')
      }

      if (status >= 500) {
        toast.error('Erro interno do servidor')
      }
    }
    return Promise.reject(error)
  }
)

export default api
